import { IBoundingBox, IGroupElementDataNode, IPosition, ISize, OneOfDataNodes, ITimelineNode, OneOfGroupDataNodes } from '../../domain';
import { forEachParentNode, isGroupDataNode } from './helpers';
import { ElementSelection } from './selection';

export function cloneNodes(nodes: OneOfDataNodes[]): OneOfDataNodes[] {
    return nodes.map(node => {
        if (isGroupDataNode(node)) {
            return node.copy(true);
        }
        return node;
    });
}

export function hasLockedAncestor(node: OneOfDataNodes): boolean {
    let hasLockedParent = false;

    forEachParentNode(node, parent => {
        if (parent.locked) {
            hasLockedParent = true;
            return true;
        }
    });

    return hasLockedParent;
}

export function hasHiddenAncestor(node: OneOfDataNodes): boolean {
    let hasHiddenParent = false;

    forEachParentNode(node, parent => {
        if (parent.hidden) {
            hasHiddenParent = true;
            return true;
        }
    });

    return hasHiddenParent;
}

/**
 * Looks for the first expanded ancestor of node
 */
export function findExpandedAncestor(
    node: OneOfDataNodes,
    nodeList: ITimelineNode[]
): IGroupElementDataNode | undefined {
    let ancestor: IGroupElementDataNode | undefined;

    forEachParentNode(node, parent => {
        const isParentExpanded = nodeList.some(tl => tl.node.__parentNode?.id === parent.id && !tl.collapsed);
        if (isGroupDataNode(parent) && isParentExpanded) {
            ancestor = parent;
            return true;
        }
    });

    return ancestor;
}

/**
 * Checks if parent of node is selected
 */
export function isParentNodeSelected(
    node: OneOfDataNodes,
    selection: ElementSelection
): boolean {
    return selection.nodes.some(n => isGroupDataNode(n) && node.__parentNode?.id === n.id);
}


export function getBoundingBoxOfGroup(node: IGroupElementDataNode): IBoundingBox {
    let x1, y1, x2, y2;
    node.elements.forEach(element => {
        if (x1 === undefined || element.x < x1) {
            x1 = element.x;
        }
        if (y1 === undefined || element.y < y1) {
            y1 = element.y;
        }
        if (x2 === undefined || element.x + element.width > x2) {
            x2 = element.x + element.width;
        }
        if (y2 === undefined || element.y + element.height > y2) {
            y2 = element.y + element.height;
        }
    });

    return {
        x: x1,
        y: y1,
        width: x2 - x1,
        height: y2 - y1
    };
}

export function getSizeAndPositionOfNode(node: OneOfDataNodes): ISize & IPosition {
    if (isGroupDataNode(node)) {
        const box = getBoundingBoxOfGroup(node);
        const { x, y, width, height } = box;
        return { x, y, width, height };
    } else {
        const { x, y, width, height } = node;
        return { x, y, width, height };
    }
}

export function hasMaximumGroupNesting(currentParent: OneOfGroupDataNodes): boolean {
    const MAX_NESTED_GROUPS = 7;
    let parent: OneOfGroupDataNodes | undefined = currentParent;
    let parentAmount = 0;

    while (parent && isGroupDataNode(parent) && parent !== parent.__parentNode) {
        parent = parent.__parentNode;
        parentAmount++;
    }

    return parentAmount >= MAX_NESTED_GROUPS;
}
